<template>
  <div>
    <empty-state
      v-if="loading || !data || data.length === 0"
      :loading="loading"
      :title="loading ? $t('loading') : $t('no_income_data')"
      :subtitle="!loading ? $t('no_income_found') : ''"
      icon="mdi-cash-multiple"
    />
    <div v-else class="pa-1">
      <!-- Mobile View -->
      <template v-if="device.isMobile">
        <v-card
          v-for="(item, index) in customSearch(data, mobileHeaders, searchQuery)"
          :key="index"
          class="mb-3 ordera-box-shadow"
          outlined
        >
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption grey--text">
                {{ $t("date") }}
              </v-list-item-subtitle>
              <v-list-item-title class="font-weight-bold">
                {{ localeDate(item.date) }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <div class="text-right">
                <div class="text-caption grey--text">{{ $t("number_of_orders") }}</div>
                <div class="font-weight-bold">{{ item.orders.length }}</div>
              </div>
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-caption grey--text">
                {{ $t("amount") }}
              </v-list-item-subtitle>
              <v-list-item-title class="font-weight-bold primary--text text-h6">
                {{ item.amount.toFixed(2) }} KM
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Expandable orders list -->
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="grey lighten-4">
                <span class="font-weight-medium">
                  {{ $t("view_orders") }} ({{ item.orders.length }})
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list dense>
                  <template v-for="(order, orderIndex) in item.orders">
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="text-caption">
                          ID: {{ order.id }} | {{ order.delivery_time }}
                        </v-list-item-subtitle>
                        <v-list-item-title>
                          <v-chip
                            x-small
                            :color="getStatusColor(order.state)"
                            class="mr-2"
                          >
                            {{ $t(order.state) }}
                          </v-chip>
                          <span class="font-weight-bold"
                            >{{ order.total.toFixed(2) }} KM</span
                          >
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          icon
                          small
                          color="orange"
                          @click="$emit('show-details', { order, item })"
                        >
                          <v-icon small>mdi-clipboard-text</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider
                      v-if="orderIndex < item.orders.length - 1"
                      :key="'div-' + order.id"
                    ></v-divider>
                  </template>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <!-- Total footer for mobile -->
        <v-card class="mt-3 ordera-box-shadow" outlined>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">
                {{ $t("total").toUpperCase() }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t("number_of_orders") }}: {{ totalOrderCount }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <div class="text-h6 font-weight-bold primary--text">
                {{ totalIncome.toFixed(2) }} KM
              </div>
            </v-list-item-action>
          </v-list-item>
        </v-card>
      </template>

      <!-- Desktop View -->
      <v-data-table
        v-else
        :headers="headers"
        :items="customSearch(data, headers, searchQuery)"
        class="striped-table ordera-box-shadow"
        :loading="loading"
        hide-default-footer
        disable-pagination
        show-expand
        :expanded.sync="expandedItems"
      >
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.date="{ item }">
          {{ localeDate(item.date) }}
        </template>
        <template v-slot:item.orderCount="{ item }">
          {{ item.orders.length }}
        </template>
        <template v-slot:item.amount="{ item }">
          <span class="font-weight-bold">{{ item.amount.toFixed(2) }} KM</span>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <v-simple-table dense class="grey lighten-4">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{{ $t("time") }}</th>
                    <th>{{ $t("status") }}</th>
                    <th class="text-right">{{ $t("amount") }} KM</th>
                    <th>{{ $t("order_details") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in item.orders" :key="order.id">
                    <td>{{ order.id }}</td>
                    <td>{{ order.delivery_time }}</td>
                    <td>
                      <v-chip small :color="getStatusColor(order.state)">
                        {{ $t(order.state) }}
                      </v-chip>
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ order.total.toFixed(2) }} KM
                    </td>
                    <td>
                      <a
                        href="#"
                        class="text-decoration-none orange--text"
                        @click.prevent="$emit('show-details', { order, item })"
                      >
                        <v-icon small color="orange">mdi-clipboard-text</v-icon>
                        {{ $t("order_details") }}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </td>
        </template>

        <template v-slot:body.append>
          <tr class="font-weight-bold grey lighten-3">
            <td></td>
            <td>{{ $t("total").toUpperCase() }}</td>
            <td class="text-center">{{ totalOrderCount }}</td>
            <td class="text-right">
              <span class="font-weight-bold">{{ totalIncome.toFixed(2) }} KM</span>
            </td>
            <td></td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";

export default {
  name: "IncomeTable",
  components: {
    EmptyState,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      expandedItems: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("date"), value: "date", sortable: false },
        {
          text: this.$t("number_of_orders"),
          value: "orderCount",
          sortable: false,
          align: "center",
        },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
        { text: "", value: "data-table-expand" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("date"), value: "date", sortable: false },
        {
          text: this.$t("number_of_orders"),
          value: "orderCount",
          sortable: false,
        },
        { text: this.$t("amount"), value: "amount", sortable: false },
      ];
    },
    totalIncome() {
      return this.data.reduce((sum, item) => sum + item.amount, 0);
    },
    totalOrderCount() {
      return this.data.reduce((sum, item) => sum + item.orders.length, 0);
    },
  },
  methods: {
    getStatusColor(status) {
      const colors = {
        pending: "orange",
        accepted: "blue",
        "in-progress": "purple",
        ready: "cyan",
        "on-hold": "amber",
        delivered: "green",
        completed: "teal",
        cancelled: "red",
        deleted: "grey",
      };
      return colors[status] || "grey";
    },
  },
};
</script>
